import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '@/i18n/LanguageContext';
import { Heart, ArrowUpRight } from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo';
import { copyWithToast } from '@/lib/shareLaunch';

const footerCopy: Record<string, { tagline: string; linksTitle: string; legalTitle: string }> = {
  en: { tagline: 'While They\'re Still Here', linksTitle: 'Links', legalTitle: 'Legal' },
  es: { tagline: 'Mientras Aún Están Aquí', linksTitle: 'Enlaces', legalTitle: 'Legal' },
  fr: { tagline: 'Tant Qu\'ils Sont Encore Là', linksTitle: 'Liens', legalTitle: 'Légal' },
  pt: { tagline: 'Enquanto Ainda Estão Aqui', linksTitle: 'Links', legalTitle: 'Legal' },
  ar: { tagline: 'بينما لا يزالون هنا', linksTitle: 'روابط', legalTitle: 'قانوني' },
  ja: { tagline: 'まだここにいるうちに', linksTitle: 'リンク', legalTitle: '法的情報' },
  ko: { tagline: '아직 곁에 있을 때', linksTitle: '링크', legalTitle: '법적 정보' },
  id: { tagline: 'Selagi Mereka Masih Ada', linksTitle: 'Tautan', legalTitle: 'Hukum' },
};

export function Footer() {
  const { t, language } = useLanguage();
  const fCopy = footerCopy[language] || footerCopy.en;
  const location = useLocation();
  const navigate = useNavigate();

  const handleMailto = (email: string, subject?: string) => () => {
    const mailto = subject
      ? `mailto:${email}?subject=${encodeURIComponent(subject)}`
      : `mailto:${email}`;
    window.location.href = mailto;
    // Fallback: if mailto didn't navigate away, copy email to clipboard
    setTimeout(() => {
      copyWithToast(
        email,
        ({
          en: 'Email copied — paste it in your email app',
          es: 'Email copiado — pégalo en tu app de correo',
          fr: 'Email copié — collez-le dans votre app',
          pt: 'Email copiado — cole no seu app de email',
          ar: 'تم نسخ البريد — الصقه في تطبيق البريد',
          ja: 'メールアドレスをコピーしました',
          ko: '이메일 주소가 복사되었습니다',
          id: 'Email disalin — tempel di aplikasi email Anda',
        } as Record<string, string>)[language] || 'Email copied — paste it in your email app',
        ({
          en: 'Could not copy email',
          es: 'No se pudo copiar',
          fr: 'Impossible de copier',
          pt: 'Não foi possível copiar',
          ar: 'تعذر النسخ',
          ja: 'コピーできませんでした',
          ko: '복사할 수 없습니다',
          id: 'Gagal menyalin',
        } as Record<string, string>)[language] || 'Could not copy email',
      );
    }, 800);
  };

  const handleSectionClick = (sectionId: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (location.pathname === '/') {
      const section = document.getElementById(sectionId);
      section?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        const section = document.getElementById(sectionId);
        section?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const footerLinks = [
    { to: '/about', label: t.footer.about },
    { to: '/faq', label: t.footer.faq },
    { onClick: handleSectionClick('how-to-use'), label: t.footer.howToUse },
    { onClick: handleSectionClick('pricing'), label: t.footer.pricing },
    { to: '/gift-store', label: t.footer.giftStore },
    { to: '/refer', label: ({
      en: 'Tell a Friend', es: 'Invita a un Amigo', fr: 'Parlez-en à un Ami', pt: 'Indique um Amigo',
      ar: 'أخبر صديقًا', ja: '友達に教える', ko: '친구에게 알리기', id: 'Beritahu Teman',
    } as Record<string, string>)[language] || 'Tell a Friend' },
  ];

  const legalLinks = [
    { to: '/privacy', label: t.footer.privacy },
    { to: '/terms', label: t.footer.terms },
  ];

  return (
    <footer className="relative overflow-hidden">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-hero-gradient" />
      <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
      
      {/* Decorative glow */}
      <motion.div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[200px] w-[400px] rounded-full bg-primary/6 blur-[80px]"
        animate={{ opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="container relative z-10 py-14">
        <div className="grid gap-10 md:grid-cols-4">
          {/* Brand */}
          <div className="md:col-span-2">
            <BrandLogo size="md" />
            <p className="mt-3 text-muted-foreground max-w-sm leading-relaxed">
              {t.footer.tagline}
            </p>
          </div>

          {/* Links */}
          <div>
            <h3 className="font-heading font-semibold mb-5 text-foreground text-sm uppercase tracking-wider">{fCopy.linksTitle}</h3>
            <ul className="space-y-3">
              {footerLinks.map((link, i) => (
                <li key={i}>
                  {link.to ? (
                    <Link to={link.to} className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center gap-1 group">
                      {link.label}
                      <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  ) : (
                    <button onClick={link.onClick} className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center gap-1 group">
                      {link.label}
                      <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Legal & Contact */}
          <div>
            <h3 className="font-heading font-semibold mb-5 text-foreground text-sm uppercase tracking-wider">{fCopy.legalTitle}</h3>
            <ul className="space-y-3">
              {legalLinks.map((link, i) => (
                <li key={i}>
                  <Link to={link.to} className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center gap-1 group">
                    {link.label}
                    <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
              <li>
                <button onClick={handleMailto('support@say-it-now.app')} className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center gap-1 group">
                {({
                  en: 'Support', es: 'Soporte', fr: 'Assistance', pt: 'Suporte',
                  ar: 'الدعم', ja: 'サポート', ko: '지원', id: 'Dukungan',
                } as Record<string, string>)[language] || 'Support'}
                  <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              </li>
              <li>
                <button onClick={handleMailto('support@say-it-now.app', 'Privacy Inquiry')} className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center gap-1 group">
                {({
                  en: 'Privacy Inquiries', es: 'Privacidad', fr: 'Confidentialité', pt: 'Privacidade',
                  ar: 'استفسارات الخصوصية', ja: 'プライバシーに関するお問い合わせ', ko: '개인정보 문의', id: 'Pertanyaan Privasi',
                } as Record<string, string>)[language] || 'Privacy Inquiries'}
                  <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-border/30">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-sm text-muted-foreground">
                © {new Date().getFullYear()} Say It NOW. {({ en: 'All rights reserved.', es: 'Todos los derechos reservados.', pt: 'Todos os direitos reservados.', fr: 'Tous droits réservés.', ar: 'جميع الحقوق محفوظة.', ja: '全著作権所有。', ko: '모든 권리 보유.', id: 'Hak cipta dilindungi.' } as Record<string, string>)[language] || 'All rights reserved.'}
              </p>
              <p className="text-xs text-muted-foreground/50 mt-1">
                {({
                  en: 'Built with care for those who care.',
                  es: 'Hecho con cariño para quienes se preocupan.',
                  pt: 'Feito com carinho para quem se importa.',
                  fr: 'Conçu avec soin pour ceux qui comptent.',
                  ar: 'صُنع بعناية لمن يهتم.',
                  ja: '大切な人のために、心を込めて。',
                  ko: '소중한 사람을 위해 정성을 담아.',
                  id: 'Dibuat dengan hati untuk mereka yang peduli.',
                } as Record<string, string>)[language] || 'Built with care for those who care.'}
              </p>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground/60">
              <span>{({
                en: 'Made with', es: 'Hecho con', fr: 'Fait avec', pt: 'Feito com',
                ar: 'صنع بـ', ja: '心を込めて', ko: '사랑을 담아', id: 'Dibuat dengan',
              } as Record<string, string>)[language] || 'Made with'}</span>
              <Heart className="h-3 w-3 text-primary/60" fill="currentColor" />
              <span>{({
                en: 'for the people you love', es: 'para las personas que amas', fr: 'pour les gens que vous aimez', pt: 'para as pessoas que você ama',
                ar: 'للأشخاص الذين تحبهم', ja: '大切な人のために', ko: '소중한 사람을 위해', id: 'untuk orang-orang yang Anda cintai',
              } as Record<string, string>)[language] || 'for the people you love'}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
